import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";

const SettingsContext = createContext(null);

const FALLBACK = {
  publisher_name: "",
  publisher_pro: "BMI",
  publisher_ipi: "",
  brand_color: "#43295F",
  font_family: "Plus Jakarta Sans",
  dark_mode: false,
  lists: { labels: [], distributors: [], pros: ["BMI", "ASCAP", "SESAC", "SOCAN", "JACAP", "PRS"] },
  swatches: ["#43295F", "#1F6F5C", "#2D5B99", "#9A3A58", "#B0842B", "#C2622E", "#4C86C9", "#6FBF73"],
  societies: [
    { key: "PUBD", label: "PUB(D)" },
    { key: "JAMMS", label: "JAMMS" },
    { key: "BMI", label: "BMI" },
    { key: "MLC", label: "MLC" },
    { key: "HFA", label: "HFA" },
    { key: "MR", label: "MR" },
  ],
  production_options: {
    voice: ["No", "In process", "Yes"],
    harmony: ["No", "Pending", "In process", "Yes"],
    mix: ["Pending", "In process", "Completed", "Finalized"],
    mastered: ["Pending", "In process", "Completed"],
  },
  released_options: ["No", "Need changes", "Yes"],
};

export const FONTS = ["Plus Jakarta Sans", "Inter", "DM Sans", "Outfit"];

/** "#43295F" -> {h, s, l} */
export function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return { h: 269, s: 40, l: 27 };
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** Pushes the workspace's accent colour onto the CSS variables every brand-* utility reads. */
function applyTheme(hex, dark, font) {
  const { h, s, l } = hexToHsl(hex);
  const root = document.documentElement;
  const st = root.style;
  root.classList.toggle("dark", !!dark);
  const brand = dark ? `${h} ${Math.max(s - 6, 30)}% ${Math.min(l + 34, 72)}%` : `${h} ${s}% ${Math.min(l, 45)}%`;
  const hover = dark ? `${h} ${Math.max(s - 6, 30)}% ${Math.min(l + 42, 80)}%` : `${h} ${s}% ${Math.max(Math.min(l, 45) - 8, 12)}%`;
  const light = dark ? `${h} 30% 17%` : `${h} ${Math.min(s, 40)}% 94%`;
  const ring = dark ? `${h} 30% 30%` : `${h} ${Math.min(s, 40)}% 85%`;
  const fg = dark ? `${h} 25% 88%` : `${h} ${s}% 22%`;
  st.setProperty("--brand", brand);
  st.setProperty("--brand-hover", hover);
  st.setProperty("--brand-light", light);
  st.setProperty("--brand-ring", ring);
  st.setProperty("--primary", brand);
  st.setProperty("--ring", brand);
  st.setProperty("--secondary", light);
  st.setProperty("--secondary-foreground", fg);
  st.setProperty("--accent", light);
  st.setProperty("--accent-foreground", fg);
  st.setProperty("--app-font", `"${font || "Plus Jakarta Sans"}"`);
  try {
    window.localStorage.setItem(THEME_KEY, JSON.stringify({ hex, dark: !!dark, font: font || "" }));
  } catch {
    /* private browsing */
  }
}

// Paint the last colours (and dark mode) before the first frame, so a refresh never
// flashes the standard purple / light theme while settings load.
const THEME_KEY = "mt_theme";
try {
  const t = JSON.parse(window.localStorage.getItem(THEME_KEY) || "null");
  if (t?.hex) applyTheme(t.hex, t.dark, t.font);
} catch {
  /* nothing remembered yet */
}

const CACHE_PREFIX = "settings_cache:";
function readCache(id) {
  if (!id) return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + id);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCache(id, v) {
  try {
    window.localStorage.setItem(CACHE_PREFIX + id, JSON.stringify(v));
  } catch {
    /* private browsing */
  }
}

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();
  const [settings, setSettings] = useState(() => readCache(tenantId) || FALLBACK);
  const [loaded, setLoaded] = useState(() => !!readCache(tenantId));
  const current = useRef(tenantId);

  useEffect(() => {
    current.current = tenantId;
    const cached = readCache(tenantId);
    setSettings(cached || FALLBACK);
    setLoaded(!!cached);
  }, [tenantId]);

  const refresh = useCallback(async () => {
    if (!user || !tenantId) return;
    const askedFor = tenantId;
    try {
      const { data } = await api.get("/settings");
      if (current.current !== askedFor) return;
      const merged = { ...FALLBACK, ...data, lists: { ...FALLBACK.lists, ...(data.lists || {}) } };
      setSettings(merged);
      writeCache(askedFor, merged);
      setLoaded(true);
    } catch {
      /* keep what we have */
    }
  }, [user, tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // While we're still finding out which workspace this is, keep the colours already on
  // screen (remembered from last time) instead of flashing the standard purple.
  const waiting = !tenantId && (user === null || tenantLoading);
  useEffect(() => {
    if (waiting) return;
    applyTheme(settings.brand_color, settings.dark_mode, settings.font_family);
  }, [settings.brand_color, settings.dark_mode, settings.font_family, waiting]);

  const societyLabel = (key) => settings.societies.find((s) => s.key === key)?.label || key;

  return (
    <SettingsContext.Provider value={{ settings, setSettings, refresh, loaded, societyLabel }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
